import React, { useState, useRef, useEffect } from "react";
import { ui } from "../constants";
import { Tooltip } from "./Tooltip";
import styled from "@emotion/styled";
import { ViewTitle } from "./ViewTitle";
import { Button } from "./Button";
import { Publish } from "@mui/icons-material";
import { Notification } from "./Various";
import { useUser } from "./UserProvider";
import { useSettings } from "./SettingsProvider";
import { useUserData } from "./UserDataProvider";

const Main = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  grid-template-columns: minmax(0, 1fr);
`

const RotatedPublishIcon = styled(Publish)`
  transform: rotate(180deg);
`;

const ExportTypeButtons = styled.div`
  display: inline-flex;
  margin-top: 7px;
  overflow: hidden;
`;

const DownloadDiv = styled.div`
  display: flex;
  margin-top: 10px;
`;

const DownloadButton = styled(Button)`
  margin-left: 0px;
`;

const DownloadTypeButtons = styled.div`
  display: inline-flex;
  overflow: hidden;
`;

const DownloadFormatButtons = styled.div`
  display: inline-flex;
  margin-left: 15px;
  overflow: hidden;
`;

const ClipboardButton = styled(Button)`
  float: right;
`;

const WarningAPILink = styled.div`
  text-decoration: underline;
  border-bottom: 0px solid;
  font-weight: 700;
  color: black;
  margin: 0 0 0 0px;
  cursor: default;
  padding-left: 0em;
  padding-right: 0em;
  margin-bottom: 0em;
`;

const BottomDiv = styled.div`
  display: flex;
  margin-top: 10px;
  height: 30px;
  align-items: flex-start;
`;

const BottomFlexGrow = styled.div`
  flex-grow: 1;
`;

const ExportTextArea = styled.textarea`
  overflow-y: auto;
  margin-top: 15px;
  min-height: 0;
  max-height: 100%;
  resize: none;
  outline: none;
  padding: 5px 5px 5px 5px;
  width: 100%;
  border: 0px solid;
  box-shadow: ${ui.boxShadow};
`;

const DataWarning = styled.div`
  margin-top: 5px;
  margin-bottom: 2px;
  font-weight: 400;
`;

export const ExportView = () => {
  const { auth } = useUser();
  const {
    settings: { lang },
  } = useSettings();
  const { activeSet } = useUserData();
  const [exportType, setExportType] = useState(
    localStorage.getItem("exportType") || "Text",
  );
  const [downloadFormat, setDownloadFormat] = useState(
    localStorage.getItem("downloadFormat") || "CSV",
  );
  const [downloadType, setDownloadType] = useState("Time series");
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  const textRef = useRef();

  useEffect(() => {
    localStorage.setItem("exportType", exportType);
  }, [exportType]);

  useEffect(() => {
    localStorage.setItem("downloadFormat", downloadFormat);
  }, [downloadFormat]);

  const copyToClipboard = (event) => {
    setShowCopyNotification(true);
    setTimeout(() => setShowCopyNotification(false), 500);
    textRef.current.select();
    document.execCommand("copy");
    event.target.focus();
  };

  const owner = auth.loggedIn ? auth.username : auth.guest

  if(!activeSet) {
    var keyText = "";
  }
  else if (exportType === "R-Code") {
    var keyText = `${auth.loggedIn ? '' : 'tsdbapi::set_config(access_type="public")\n'}` +
      `tsl <- tsdbapi::read_collection_ts("${activeSet}", owner="${owner}")\n` +
      `md <- tsdbapi::read_collection_ts_metadata("${activeSet}", owner="${owner}", locale="${lang}")`;
  }
  else if (exportType === "Python-Code") {
    var keyText = `from tsdbapi import read_ts, read_ts_metadata${auth.loggedIn ? '' : ', set_config'}\n\n` +
      `${auth.loggedIn ? '' : 'set_config(access_type="public")\n'}` +
      `tsdf = read_collection_ts("${activeSet}", owner="${owner}")\n` +
      `md = read_collection_ts_metadata("${activeSet}", owner="${owner}", locale="${lang}")`;
  } 
  else if (exportType === "Download-Link") {
    var keyText = `https://tsdb-api.kof.ethz.ch/v2/collections/${owner}/${encodeURIComponent(encodeURIComponent(activeSet))}/ts${downloadType === "Metadata" ? "/metadata" : ""}?mime=${downloadFormat.toLowerCase()}`;
    if(downloadType === "Metadata") keyText += `&locale=${lang}`;
    if(!auth.loggedIn) keyText += `&access_type=public`;
  }

  const isLoggedOut = !auth.loggedIn;

  return (
    <Main>
      <div>
        <ViewTitle label="Export" />
        <ExportTypeButtons>
          <Button
            tooltip={ui.tooltips.RCode[lang]}
            ariaPressed={exportType === "R-Code"}
            onMouseDown={() => setExportType("R-Code")}
          >
            R-Code
          </Button>
          <Button
            tooltip={ui.tooltips.pythonCode[lang]}
            ariaPressed={exportType === "Python-Code"}
            onMouseDown={() => setExportType("Python-Code")}
          >
            Python-Code
          </Button>
          <Button
            tooltip={ui.tooltips.downloadLink[lang]}
            ariaPressed={exportType === "Download-Link"}
            onMouseDown={() => setExportType("Download-Link")}
          >
            Download-Link
          </Button>
        </ExportTypeButtons>
        {exportType === "Download-Link" && (
          <DownloadDiv>
            <DownloadTypeButtons>
              <Button
                tooltip={ui.tooltips.downloadTS[lang]}
                ariaPressed={downloadType === "Time series"}
                onMouseDown={() => setDownloadType("Time series")}
              >
                {ui.labels.exportTS[lang]}
              </Button>
              <Button
                tooltip={ui.tooltips.downloadMeta[lang]}
                ariaPressed={downloadType === "Metadata"}
                onMouseDown={() => setDownloadType("Metadata")}
              >
                {ui.labels.exportMeta[lang]}
              </Button>
            </DownloadTypeButtons>
            <DownloadFormatButtons>
              <Button
                tooltip={ui.tooltips.downloadCSV[lang]}
                ariaPressed={downloadFormat === "CSV"}
                onMouseDown={() => setDownloadFormat("CSV")}
              >
                CSV
              </Button>
              <Button
                tooltip={ui.tooltips.downloadXLSX[lang]}
                ariaPressed={downloadFormat === "XLSX"}
                onMouseDown={() => setDownloadFormat("XLSX")}
              >
                XLSX
              </Button>
              <Button
                tooltip={ui.tooltips.downloadJSON[lang]}
                ariaPressed={downloadFormat === "JSON"}
                onMouseDown={() => setDownloadFormat("JSON")}
              >
                JSON
              </Button>
            </DownloadFormatButtons>
          </DownloadDiv>
        )}
      </div>
      <ExportTextArea
        //rows={keyText.split(/\r\n|\r|\n/).length}
        value={keyText}
        readOnly
        ref={textRef}
      />
      <BottomDiv>
        {exportType === "Download-Link" && 
          <DownloadButton
            tooltip={ui.tooltips.download[lang]}
            // icon={<RotatedPublishIcon />}
            log="download"
            iconPosition="left"
            onMouseDown={() => {
              if (downloadFormat === "JSON") window.open(keyText);
              else window.location.href = keyText;
            }}
          >
          Download
          </DownloadButton>}
        <BottomFlexGrow />
        <ClipboardButton
          tooltip={ui.tooltips.copyToClipboard[lang]}
          onMouseDown={(event) => copyToClipboard(event)}
        >
          {ui.labels.copyToClipboard[lang]}
        </ClipboardButton>
        <Notification show={showCopyNotification}>Copied!</Notification>
      </BottomDiv>
    </Main>
  );
};
